import React, { createContext, useContext } from 'react';
import type { ThemeContextType, ThemeClasses, ThemeStyles } from '../types';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Imposta un tema chiaro professionale come default
  const isLightMode = true;
  const toggleTheme = () => {
    // noop: tema fisso
    return;
  };

  const getThemeClasses = (): ThemeClasses => {
    return {
      bg: 'theme-shell',
      text: 'theme-text',
      border: 'glass-border',

      cardBg: 'glass-card',
      cardBorder: 'glass-border',
      cardShadow: 'glass-shadow',

      inputBg: 'glass-input',
      inputBorder: 'glass-border',
      inputText: 'theme-text',

      // Classi combinate per uso diretto
      inputClasses: 'glass-input theme-text glow-focus',
      buttonPrimaryClasses: 'btn-primary glow-focus',

      buttonPrimary: 'btn-primary',
      buttonSecondary: 'btn-secondary',
      buttonDanger: 'btn-danger',
      buttonSuccess: 'btn-success',

      success: 'text-success',
      warning: 'text-warning',
      error: 'text-danger',

      hover: 'hover-glass',
      focus: 'glow-focus',

      textSecondary: 'theme-text-secondary',
      textMuted: 'theme-text-muted',
    };
  };

  const getThemeStyles = (): ThemeStyles => {
    // Tema premium raffinato: chiaro con accenti sottili e profondità elegante
    return {
      background: 'linear-gradient(135deg, #fafbfc 0%, #f3f5f8 100%)',
      backgroundGradient: 'linear-gradient(135deg, rgba(248,250,252,0.9) 0%, rgba(240,244,249,1) 60%, rgba(235,240,246,1) 100%)',
      cardBackground: '#ffffff',
      cardGlass: '#ffffff',
      text: '#0f172a', // slate-900
      textSecondary: '#475569', // slate-600
      border: 'rgba(203, 162, 77, 0.15)', // gold tenue
      primary: '#1e40af', // blue-800 (più profondo e premium)
      accent: '#cba24d',  // gold accent
      accentGradient: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #cba24d 100%)',
      success: '#16a34a',
      warning: '#d97706',
      danger: '#dc2626',
      shadow: 'rgba(15, 23, 42, 0.08)',
      hover: 'rgba(203, 162, 77, 0.05)',
      inputBackground: '#ffffff',
      cardShadow: '0 10px 30px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(203, 162, 77, 0.1)',
      buttonText: '#ffffff',
      // focus ring premium con gold tenue
      glow: '0 0 0 3px rgba(203, 162, 77, 0.2)'
    };
  };

  const getThemeDisplayName = () => {
    return isLightMode ? 'Chiaro' : 'Scuro';
  };

  return (
    <ThemeContext.Provider value={{
      theme: isLightMode ? 'chiaro' : 'scuro',
      isLightMode,
      toggleTheme,
      getThemeClasses,
      getThemeStyles,
      getThemeDisplayName
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
